<template>
  <div class="ml-3">
    <v-overlay absolute v-if="loading">
      <v-progress-circular
          indeterminate
          :size="50"
          :width="5"
          color="primary"
      >
      </v-progress-circular>
    </v-overlay>

    <v-card class="pa-0">
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>

        <v-row>
          <v-col>
            <v-select
              v-bind:label="$t('Group')"
              v-model="filter.group"
              :items="groupList"
              item-text="text"
              item-value="id"
              @change="updateGrid"
              clearable
            ></v-select>
          </v-col>
          <v-col>
            <BaseDatePicker
              v-model="filter.from"
              :label="$t('From')"
              clearable
              @change="updateGrid"
            />
          </v-col>
          <v-col>
            <BaseDatePicker
              v-model="filter.to"
              :label="$t('To')"
              clearable
              @change="updateGrid"
            />
          </v-col>
          <v-col>
            <v-autocomplete
              v-bind:label="$t('User')"
              v-model="filter.userIds"
              :items="userList"
              item-text="text"
              item-value="id"
              multiple
              @change="updateGrid"
              clearable
            />
          </v-col>
          <v-col>
            <v-autocomplete
              v-bind:label="$t('Type')"
              v-model="filter.typeIds"
              :items="typeList"
              item-text="text"
              item-value="id"
              multiple
              @change="updateGrid"
              clearable
            />
          </v-col>
          <v-col>
            <v-autocomplete
              v-bind:label="$t('Source')"
              v-model="filter.sourceIds"
              :items="sourceList"
              item-text="text"
              item-value="id"
              multiple
              @change="updateGrid"
              clearable
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card class="pa-0 mt-0">
      <v-card-text class="pt-0 mt-0">
        <v-simple-table dense>
          <thead>
          <tr>
            <th class="text-left" @click="onTableHeadClicked('Date')">
              <h4 href="javascript:void(0)" style="cursor: pointer">
                {{ this.$t("Date") }}
                <span v-if="'Date' === paginationSetting.orderBy && paginationSetting.desc">↓</span>
                <span v-if="'Date' === paginationSetting.orderBy && !paginationSetting.desc">↑</span>
              </h4>
            </th>
            <th class="text-left">
              <h4 href="javascript:void(0)">
                {{ this.$t("Type") }}
              </h4>
            </th>
            <th class="text-left">
              <h4>
                {{ this.$t("User") }}
              </h4>
            </th>
            <th class="text-left">
              <h4>
                {{ this.$t("Description") }}
              </h4>
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-if="dataSource.length === 0">
            <td colspan="9999" class="text-center table-no-data">{{ this.$t("NoData") }}</td>
          </tr>
          <tr v-for="(column, index) in dataSource" :key="index">
            <td>{{ calculateCellValue(column.Date) }}</td>
            <td>{{ column.Type }}</td>
            <td>{{ column.User }}</td>
            <td>{{ column.Description }}</td>
          </tr>
          </tbody>
        </v-simple-table>

        <PaginationComponent
            :ref="paginationComponentKey"
            :totalPages="totalPages"
            :totalItems="totalItems"
            :pageSizes="[25,50,100,500]"
            @on-page-change="getData">
        </PaginationComponent>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import AuditService from "@/services/AuditService";
import UserService from "@/services/UserService";
import AuditType from "@/ecm/variables/AuditType.js";
import PaginationSetting from "@/ecm/models/PaginationSetting";
import Utils from "@/ecm/common/Utils";
import DocumentService from "@/services/DocumentService";
import PaginationComponent from "@/components/base/PaginationComponent.vue";

export default {
  components: {PaginationComponent},
  data() {
    return {
      title: this.$t("Audits"),
      paginationSetting: new PaginationSetting(),
      groupList: [
        { id: "1", text: this.$t("Administration") },
        { id: "2", text: this.$t("Documents") },
        { id: "3", text: this.$t("Searches") },
        { id: "4", text: this.$t("Flows") },
        { id: "5", text: this.$t("Delegations") },
      ],
      userList: [],
      typeList: [],
      sourceList: [],
      paginationComponentKey: "paginationComponentKey",
      filter: {
        group: null,
        from: null,
        to: null,
        userIds: null,
        typeIds: null,
        sourceIds: null,
      },
      dataSource: [],
      totalItems: 1,
      totalPages: 1,
      loading: false
    };
  },
  created() {
    AuditService.getTypes().then((data) => {
      this.typeList = data;
    });
    AuditService.getSources().then((data) => {
      this.sourceList = data;
    });
    UserService.getList().then((data) => {
      this.userList = data;
    });
    this.$nextTick(() => {
      this.paginationComponentInstance.reset(true);
    });
  },
  methods: {
    getData(paginationOptions) {
      this.loading = true;

      if (!this.paginationSetting.orderBy) {
        this.paginationSetting.orderBy = "Date";
        this.paginationSetting.desc = true;
      }

      if (paginationOptions && paginationOptions.page)
        this.paginationSetting.page = paginationOptions.page;

      if (paginationOptions && paginationOptions.pageSize)
        this.paginationSetting.pageSize = paginationOptions.pageSize;

      this.paginationSetting.dateFrom = Utils.date.toIsoFromUserFormat(this.filter.from);
      this.paginationSetting.dateTo = Utils.date.toIsoFromUserFormat(this.filter.to);
      this.paginationSetting.group = parseInt(this.filter.group);
      this.paginationSetting.userIDs = this.filter.userIds;
      this.paginationSetting.auditTypeIDs = this.filter.typeIds;
      this.paginationSetting.auditSourceIDs = this.filter.sourceIds;

      AuditService.getGrid(this.paginationSetting).then((data) => {
        this.totalPages = Math.ceil(
            data.Count / this.paginationSetting.pageSize
        );
        if(this.totalPages === 0)
          this.totalPages = 1;
        this.totalItems = data.Count;
        this.dataSource = data.Result;
        if (this.dataSource.length === 0 && this.paginationSetting.page > 1) {
          // If it's empty, go back to the previous page
          this.$refs.paginationComponentKey.changePage(this.paginationSetting.page - 1);
        }
        this.loading = false;
      });
    },
    updateGrid() {
      this.filterTypesByGroups();
      this.paginationComponentInstance.reset();
    },
    filterTypesByGroups() {
      AuditService.getTypes().then((data) => {
        this.typeList = data;
        let typesInGroup = this.getTypesOfGroup();

        if (typesInGroup.length !== 0) {
          let newTypes = [];
          for (let l = 0; l < this.typeList.length; l++) {
            let id = parseInt(this.typeList[l].id);
            if (typesInGroup.includes(id)) {
              newTypes.push(this.typeList[l]);
            }
          }
          this.typeList = newTypes;
        }
      });
    },
    getTypesOfGroup() {
      switch (this.filter.group) {
        case this.groupList[0].id:
          return [
            AuditType.ADMINISTRATION_DELETE,
            AuditType.ADMINISTRATION_UPDATE,
            AuditType.ADMINISTRATION_CREATE,
          ];
        case this.groupList[1].id:
          return [
            AuditType.DOCUMENT_DELETE,
            AuditType.DOCUMENT_INSERT,
            AuditType.DOCUMENT_UPDATE,
            AuditType.DOCUMENT_UPDATE_ATTACHMENT,
            AuditType.DOCUMENT_DELETE_PERMANENTLY,
            AuditType.DOCUMENT_RESTORE,
            AuditType.DOCUMENT_ARCHIVE,
            AuditType.DOCUMENT_DOWNLOAD,
            AuditType.DOCUMENT_DISPLAY,
            AuditType.DOCUMENT_PRINT,
            AuditType.DOCUMENT_AUTO_UPDATE,
            AuditType.DOCUMENT_CONTENT_TYPE_CHANGE,
            AuditType.DOCUMENT_SIGN,
            AuditType.DOCUMENT_SEND_BY_EMAIL,
            AuditType.COMMENT_CREATE,
            AuditType.COMMENT_CREATE_EXTERNAL,
            AuditType.COMMENT_UPDATE,
            AuditType.COMMENT_DELETE,
            AuditType.DOCUMENT_ATTACHMENT_INSERT,
            AuditType.DOCUMENT_ATTACHMENT_UPDATE,
            AuditType.DOCUMENT_ATTACHMENT_DELETE,
            AuditType.DOCUMENT_ATTACHMENT_DISPLAY,
            AuditType.DOCUMENT_ATTACHMENT_DOWNLOAD,
            AuditType.DOCUMENT_DISPLAY_EXTERNAL,
            AuditType.DOCUMENT_SIGN_EXTERNAL,
            AuditType.DOCUMENT_DOWNLOAD_EXTERNAL,
            AuditType.DOCUMENT_VALIDATE_EXTERNAL,
            AuditType.DOCUMENT_REJECT_EXTERNAL,
          ];
        case this.groupList[2].id:
          return [
            AuditType.USER_ADVANCED_SEARCH,
            AuditType.USER_NATURAL_SEARCH,
            AuditType.USER_BASKET_SEARCH,
            AuditType.USER_NOTIFICATION_SEARCH,
          ];
        case this.groupList[3].id:
          return [
            AuditType.FLOW_VALIDATE_ACTION,
            AuditType.FLOW_REFUSE_ACTION,
            AuditType.FLOW_DISTRIBUTE_ACTION,
          ];
        case this.groupList[4].id:
          return [
            AuditType.DELEGATION_SCHEDULED_START,
            AuditType.DELEGATION_SCHEDULED_END,
            AuditType.DELEGATION_MANUAL_START,
            AuditType.DELEGATION_MANUAL_END,
          ];
        default:
          return [];
      }
    },
    onTableHeadClicked(column) {
      if (
          this.paginationSetting.orderBy !== column ||
          this.paginationSetting.orderBy === ""
      ) {
        this.paginationSetting.desc = false;
      } else {
        this.paginationSetting.desc = !this.paginationSetting.desc;
      }

      this.paginationSetting.orderBy = column;
      this.getData();
    },
    calculateCellValue(delDate) {
      return Utils.date.toUserFormatFromIso(delDate);
    }
  },
  computed: {
    gridInstance() {
      return this.$refs[this.grid.key];
    },
    paginationComponentInstance() {
      return this.$refs[this.paginationComponentKey];
    },
  },
};
</script>